import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { CalculationBaseColumn, ICalculationBaseColumnLabel } from "store/calculationBaseColumnLabelsStore";

import { getCalculationBaseColumnOptions } from "../calculationBaseColumnUtils";
import styles from "./style.module.scss";

type CalculationBaseColumnOption = {
    value: CalculationBaseColumn;
    label: string;
};

type CalculationBaseColumnFieldProps = {
    value: CalculationBaseColumn;
    configuredLabels: ICalculationBaseColumnLabel[];
    onChange: (value: CalculationBaseColumn) => void;
    label: string;
    description?: string;
    /** Overrides the selectable options; defaults to every configured calculation-base column. */
    options?: CalculationBaseColumnOption[];
    disabled?: boolean;
    disabledText?: string;
    danger?: boolean;
};

const CalculationBaseColumnField = ({
    value,
    configuredLabels,
    onChange,
    label,
    description,
    options: optionsProp,
    disabled = false,
    disabledText,
    danger = false,
}: CalculationBaseColumnFieldProps) => {
    const [t] = useTranslation();
    const options = optionsProp ?? getCalculationBaseColumnOptions(configuredLabels, t);

    const selectedOption = useMemo(
        () => options.find((option) => option.value === value) ?? options[0] ?? null,
        [options, value],
    );

    return (
        <div
            className={`${styles["calculation-base-column-field"]}${
                danger ? ` ${styles["calculation-base-column-field--danger"]}` : ""
            }`}
        >
            <div className={styles.copy}>
                <span className={styles.label}>{label}</span>
                {description ? <p className={styles.description}>{description}</p> : null}
            </div>
            <AutocompleteFormComponent
                options={options}
                value={selectedOption}
                onChange={(_event, data: CalculationBaseColumnOption | null) => {
                    if (data) onChange(data.value);
                }}
                renderOptionText={(option: CalculationBaseColumnOption) => option.label}
                isOptionEqualToValue={(option: CalculationBaseColumnOption, current: CalculationBaseColumnOption) =>
                    option.value === current.value
                }
                disableClearable
                disabled={disabled}
                disabledText={disabledText}
                size="small"
                renderInput={(params) => <StyledTextField {...params} size="small" />}
            />
        </div>
    );
};

export default CalculationBaseColumnField;
